import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Radio,
  Table,
  InputNumber,
  Divider,
  Alert,
  Space,
  Statistic,
} from "antd";
import { toast } from "react-toastify";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import checkTokenExp from "../../utils/checkTokenExp";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import data from "../../data/rawMaterialData";
import { buildLayersForPly } from "../../utils/layerUtils";
import axios from "axios";

const { Title, Text } = Typography;

const SheetSizeRateCalculator = () => {
  const { unitType, ply: plyOptions, castingType } = data;
  const layerObj = { layerId: 0, layerType: "", materialType: "", gsm: "", bf: "", ff: "" };

  const subCategory = useSelector((state) => state.productSubCategories?.list);
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [loader, setLoader] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCoating, setIsCoating] = useState(false);
  const [layers, setLayers] = useState([]);
  const [layerSelector, setLayerSelector] = useState(false);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);
  const [printing, setPrinting] = useState({});
  const [coating, setCoating] = useState({ coatings: [] });
  const [rate, setRate] = useState(null);

  useEffect(() => {
    dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Check if user is logged in
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }

  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  const onFinish = async (values) => {
    setLoader(true);
    try {
      const formData = {
        // Sheet size inputs (main difference from L,B,H mode)
        rs: values.rs,
        cs: values.cs,
        sheetsPerBox: values.sheetsPerBox || 1,
        boxesPerSheet: values.boxesPerSheet || 1,
        sheetLength: values.sheetLength || values.rs,
        sheetWidth: values.sheetWidth || values.cs,
        // Common fields
        itemName: values.itemName,
        subcategory: values.subcategory,
        unitType: values.unitType || "inches",
        casting: values.casting || "rotary",
        quantity: values.quantity,
        ply: values.ply,
        layerSelector: layerSelector,
        layers: layers,
        isPrinting: isPrinting,
        printing: printing,
        isCoating: isCoating,
        coating: coating,
        topConversionRate: values.topConversionRate,
        boxConversionRate: values.boxConversionRate,
      };

      console.log("Sheet Size Rate Request:", formData);

      const response = await axios.post("get-single-rate/sheet-size", formData);

      if (response.data) {
        setRate(response.data);
        toast.success("Rate calculated successfully!");
      }
      setLoader(false);
    } catch (error) {
      console.error("Error calculating rate:", error);
      toast.error(error.response?.data?.message || "Failed to calculate rate");
      setLoader(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoader(false);
    const firstError = errorInfo?.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
    toast.error(firstError);
  };

  const handlePlyChange = (plyVal) => {
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(plyVal, layers, layerObj);
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  const handleSelectorChange = (e) => {
    setLayerSelector(e.target.value === "advance");
  };

  const handlePrintingChange = (e) => {
    setIsPrinting(e.target.value === "yes");
  };

  const handleCoatingChange = (e) => {
    setIsCoating(e.target.value === "yes");
  };

  // Rate breakdown table data
  const rateBreakdownColumns = [
    { title: "Parameter", dataIndex: "parameter", key: "parameter" },
    { title: "Value", dataIndex: "value", key: "value" },
    { title: "Cost (₹)", dataIndex: "cost", key: "cost", align: "right" },
  ];

  const rateBreakdownData = rate
    ? [
        { key: 1, parameter: "Top Layer Rate", value: `${rate.TOP_GSM} GSM`, cost: rate.topRate?.toFixed(2) },
        { key: 2, parameter: "Rest Layers Rate", value: `${rate.NET_GSM - rate.TOP_GSM} GSM`, cost: rate.restRate?.toFixed(2) },
        { key: 3, parameter: "Print Rate", value: isPrinting ? "Applied" : "N/A", cost: rate.printRate?.toFixed(2) || "0.00" },
        { key: 4, parameter: "Coating Rate", value: isCoating ? "Applied" : "N/A", cost: rate.coatingRate?.toFixed(2) || "0.00" },
        { key: 5, parameter: "Fixed Cost/Box", value: `Qty: ${rate.quantity}`, cost: rate.fixedCostPerBox?.toFixed(2) || "0.00" },
        { key: 6, parameter: "Box Weight", value: `${rate.boxWeight?.toFixed(3)} Kg`, cost: "-" },
      ]
    : [];

  return (
    <Fragment>
      <Row className="mr-top" justify="space-between" gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <Card title={<Title level={4}>Sheet Size Rate Calculator</Title>}>
            <Alert
              message="Direct RS/CS Input Mode"
              description="Enter sheet dimensions (RS x CS) directly instead of box dimensions (L x B x H). Use this when you know the exact sheet size."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form
              form={form}
              name="sheetSizeRateCalculator"
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 16 }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              {/* Item Name */}
              <Form.Item label="Item Name" name="itemName" style={{ marginBottom: 12 }}>
                <Input placeholder="Optional item name for reference" />
              </Form.Item>

              {/* Subcategory */}
              <Form.Item
                label="Subcategory"
                name="subcategory"
                rules={[{ required: true, message: "Select subcategory!" }]}
                style={{ marginBottom: 12 }}
              >
                <Select placeholder="Select subcategory" showSearch optionFilterProp="children">
                  {subCategory?.map((item) => (
                    <Select.Option key={item.id} value={item.name}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Divider>Sheet Dimensions</Divider>

              {/* RS and CS - Main inputs */}
              <Row gutter={16}>
                <Col xs={12}>
                  <Form.Item
                    label="RS (Reel Size)"
                    name="rs"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    rules={[{ required: true, message: "Enter RS!" }]}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} placeholder="RS in inches" />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label="CS (Cutting Size)"
                    name="cs"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    rules={[{ required: true, message: "Enter CS!" }]}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} placeholder="CS in inches" />
                  </Form.Item>
                </Col>
              </Row>

              {/* Sheets per Box (N) and Boxes per Sheet (M) */}
              <Row gutter={16}>
                <Col xs={12}>
                  <Form.Item
                    label="Sheets/Box (N)"
                    name="sheetsPerBox"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    initialValue={1}
                    tooltip="How many sheets to make 1 box"
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label="Boxes/Sheet (M)"
                    name="boxesPerSheet"
                    labelCol={{ span: 12 }}
                    wrapperCol={{ span: 12 }}
                    initialValue={1}
                    tooltip="How many boxes from 1 sheet"
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Production Details</Divider>

              {/* Quantity */}
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, message: "Enter quantity!" }]}
                style={{ marginBottom: 12 }}
              >
                <InputNumber min={1} style={{ width: "100%" }} placeholder="Number of boxes" />
              </Form.Item>

              {/* Unit Type */}
              <Form.Item label="Unit Type" name="unitType" initialValue="inches" style={{ marginBottom: 12 }}>
                <Select>
                  {unitType.map((type, i) => (
                    <Select.Option key={i} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Casting Type */}
              <Form.Item label="Casting" name="casting" initialValue="rotary" style={{ marginBottom: 12 }}>
                <Select>
                  {castingType.map((type, i) => (
                    <Select.Option key={i} value={type.value}>
                      {type.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Ply Selection */}
              <Form.Item
                label="Ply / Walls"
                name="ply"
                rules={[{ required: true, message: "Select ply!" }]}
                style={{ marginBottom: 12 }}
              >
                <Select placeholder="Select Ply" onChange={handlePlyChange}>
                  {plyOptions.map((p, i) => (
                    <Select.Option key={i} value={parseInt(p.value)}>
                      {p.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Layer Selector */}
              <LayerTemplateSelector
                ply={layers.length}
                layers={layers}
                setLayers={setLayers}
                gsm={gsm}
                setGsm={setGsm}
                bf={bf}
                setBf={setBf}
              />

              <Form.Item label="Layer Selector" name="layerSelector" style={{ marginBottom: 12 }}>
                <Radio.Group defaultValue="basic" onChange={handleSelectorChange}>
                  <Radio value="basic">Basic</Radio>
                  <Radio value="advance">Advance</Radio>
                </Radio.Group>
              </Form.Item>

              {/* Layer Configuration */}
              {!layerSelector ? (
                <LayerSelectorBasic layers={layers} setLayers={setLayers} layerSelector={layerSelector} />
              ) : (
                <LayerSelectorAdvance
                  layers={layers}
                  gsm={gsm}
                  bf={bf}
                  setLayers={setLayers}
                  setGsm={setGsm}
                  setBf={setBf}
                  layerSelector={layerSelector}
                />
              )}

              <Divider>Conversion Rates</Divider>

              {/* Conversion Rates */}
              <Row gutter={16}>
                <Col xs={12}>
                  <Form.Item
                    label="Top Rate (₹/Kg)"
                    name="topConversionRate"
                    labelCol={{ span: 14 }}
                    wrapperCol={{ span: 10 }}
                    rules={[{ required: true, message: "Enter top conversion rate!" }]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    label="Box Rate (₹/Kg)"
                    name="boxConversionRate"
                    labelCol={{ span: 14 }}
                    wrapperCol={{ span: 10 }}
                    rules={[{ required: true, message: "Enter box conversion rate!" }]}
                  >
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Printing & Coating</Divider>

              {/* Printing */}
              <Form.Item label="Printing" style={{ marginBottom: 12 }}>
                <Radio.Group value={isPrinting ? "yes" : "no"} onChange={handlePrintingChange}>
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Radio.Group>
              </Form.Item>

              {isPrinting && <PrintSelector printing={printing} setPrinting={setPrinting} />}

              {/* Coating */}
              <Form.Item label="Coating" style={{ marginBottom: 12 }}>
                <Radio.Group value={isCoating ? "yes" : "no"} onChange={handleCoatingChange}>
                  <Radio value="yes">Yes</Radio>
                  <Radio value="no">No</Radio>
                </Radio.Group>
              </Form.Item>

              {isCoating && <CoatingSelector coating={coating} setCoating={setCoating} />}

              <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                <Button type="primary" htmlType="submit" loading={loader} size="large">
                  Calculate Rate
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Results Panel */}
        <Col xs={24} md={8}>
          <Card title="Calculation Results">
            {rate ? (
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                {/* Final Rate */}
                <Card size="small" style={{ backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}>
                  <Statistic
                    title="Final Rate per Box"
                    value={rate.finalRate}
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: "#3f8600", fontSize: 28 }}
                  />
                </Card>

                {/* Sheet Info */}
                <Card size="small" title="Sheet Information">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic title="RS" value={rate.RS} suffix="in" />
                    </Col>
                    <Col span={12}>
                      <Statistic title="CS" value={rate.CS} suffix="in" />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                      <Statistic title="N (Sheets/Box)" value={rate.N} />
                    </Col>
                    <Col span={12}>
                      <Statistic title="M (Boxes/Sheet)" value={rate.M} />
                    </Col>
                  </Row>
                </Card>

                {/* GSM Info */}
                <Card size="small" title="Material">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic title="Net GSM" value={rate.NET_GSM} />
                    </Col>
                    <Col span={12}>
                      <Statistic title="Top GSM" value={rate.TOP_GSM} />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={24}>
                      <Statistic title="Box Weight" value={rate.boxWeight} precision={3} suffix="Kg" />
                    </Col>
                  </Row>
                </Card>

                {/* Cost Breakdown */}
                <Table
                  columns={rateBreakdownColumns}
                  dataSource={rateBreakdownData}
                  pagination={false}
                  size="small"
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={2}>
                        <Text strong>Total Rate</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <Text strong style={{ color: "#3f8600" }}>₹{rate.finalRate?.toFixed(2)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              </Space>
            ) : (
              <Alert message="Fill the form and click Calculate to see results" type="info" showIcon />
            )}
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default SheetSizeRateCalculator;
